import React from "react";
import { useTranslation } from "react-i18next";
import { StatCard } from "@outpave/ds";
import { Columns } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import { formatNumber } from "utils/formatNumber";
import type { PlatformRewardRuleSummary, PlatformRewardsPool } from "./types";

interface RewardsProgramSummaryProps {
  summary: PlatformRewardRuleSummary | null;
  pool: PlatformRewardsPool | null;
  loading: boolean;
}

const RewardsProgramSummary: React.FC<RewardsProgramSummaryProps> = ({
  summary,
  pool,
  loading,
}) => {
  const { t } = useTranslation();
  const code = summary?.currency ?? "usd";
  return (
    <Columns gap="var(--space-4)" count={{ xs: 2, lg: 4 }}>
      <StatCard
        title={t("Cash in pool")}
        value={formatCashFull(pool?.cash ?? 0, code)}
        caption={
          pool?.coveragePercent === null || pool?.coveragePercent === undefined
            ? undefined
            : t("PoolCoversRewards", {
                percent: Math.max(0, Math.min(pool.coveragePercent, 100)),
              })
        }
        tooltip={t("Money the pool holds at the provider")}
        loading={loading}
      />
      <StatCard
        title={t("Blended earn rate")}
        value={
          summary?.blendedRatePercent === null ||
          summary?.blendedRatePercent === undefined
            ? t("No live rules")
            : `${formatNumber(Math.round(summary.blendedRatePercent * 100) / 100)}%`
        }
        caption={t("Average across live rules")}
        loading={loading}
      />
      <StatCard
        title={t("Accruing per month")}
        value={formatCashFull(summary?.accruedLast30Days ?? 0, code)}
        caption={t("Earned in this pool over the last 30 days")}
        loading={loading}
      />
      <StatCard
        title={t("Outstanding liability")}
        value={formatCashFull(summary?.outstanding ?? 0, code)}
        caption={t("EarnedNotYetRedeemed")}
        loading={loading}
      />
    </Columns>
  );
};

export default RewardsProgramSummary;
